import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  setCurrentProject,
  setProjects,
  addProject,
  removeProject,
} from "../store/slices/projectSlice";

const baseUrl = process.env.REACT_APP_API_URL || "https://validaite.io/api";

const ProjectsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state - projects from database
  const projects = useSelector((state) => state.project.projects);

  // Helper function to get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case "Production":
        return "success";
      case "In Progress":
        return "warning";
      case "Yet-to-Start":
        return "default";
      default:
        return "default";
    }
  };

  // Local state
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/projects`);
      if (response.data.success) {
        dispatch(setProjects(response.data.data || []));
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      alert("Please enter a project name");
      return;
    }

    try {
      setCreating(true);
      const response = await axios.post(`${baseUrl}/projects`, {
        project_name: newProjectName.trim(),
      });

      if (response.data.success) {
        const newProject = response.data.data;

        // Add to Redux
        dispatch(addProject(newProject));

        // Set as current project
        dispatch(
          setCurrentProject({
            id: newProject.id,
            project_name: newProject.project_name,
          })
        );

        // Close dialog
        setShowNewProjectDialog(false);
        setNewProjectName("");

        // Navigate to Application Setup
        navigate(`/project/${newProject.id}/application-setup`);
      }
    } catch (err) {
      console.error("Error creating project:", err);
    } finally {
      setCreating(false);
    }
  };

  const handleCardClick = (project) => {
    const projectId = project.id;
    // Set current project in Redux
    dispatch(
      setCurrentProject({
        id: project.id,
        project_name: project.project_name,
      })
    );
    // Navigate to the project's application setup page
    navigate(`/project/${projectId}/application-setup`);
  };

  const handleDeleteClick = (e, project) => {
    e.stopPropagation(); // Prevent card click
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;

    try {
      setDeleting(true);
      const response = await axios.delete(
        `${baseUrl}/projects/${projectToDelete.id}`
      );

      if (response.data.success) {
        // Remove from Redux state
        dispatch(removeProject(projectToDelete.id));

        // Close dialog
        setDeleteDialogOpen(false);
        setProjectToDelete(null);
      }
    } catch (err) {
      console.error("Error deleting project:", err);
      alert("Failed to delete project. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  // Filter projects based on search
  const filteredProjects = projects.filter((p) =>
    (p.project_name || "").toLowerCase().includes(search.toLowerCase())
  );

  // Calculate stats based on actual projects
  const totalProjects = projects.length;
  const activeProjects = projects.filter((p) => p.status === "active").length;

  const dynamicStatCards = [
    { title: "Total Projects", value: totalProjects },
    { title: "Active Projects", value: activeProjects || totalProjects },
    { title: "High Risk", value: 0 },
    // { title: "Team Members", value: 11 },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f9fafb", px: 4, py: 3 }}>
      {/* HEADER */}
      <Typography variant="h5" fontWeight={700}>
        Projects
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Find your projects here...
      </Typography>

      {/* HEADER ACTIONS — Search + New Project */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <TextField
          placeholder="Search Projects"
          size="small"
          sx={{ width: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setShowNewProjectDialog(true)}
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            px: 2.5,
            fontWeight: 600,
            color: "#0094FB",
            borderColor: "#0094FB",
            backgroundColor: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#E8F3FF",
              borderColor: "#0094FB",
            },
          }}
        >
          New Project
        </Button>
      </Box>

      {/* Stats Row */}
      <Grid container spacing={4} alignItems="center" sx={{ mb: 3 }}>
        {dynamicStatCards.map((stat, i) => (
          <Grid key={i} item xs={12} sm={6} md={4}>
            <Card
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 360,
                height: 72,
                borderRadius: "12px",
                border: "1px solid #D9D9D9",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "#FFFFFF",
              }}
            >
              <Typography fontSize="14px" color="#344054">
                {stat.title}
              </Typography>
              <Typography fontSize="22px" fontWeight={400} mt={0.5}>
                {stat.value}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* PROJECT CARDS */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      ) : filteredProjects.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <Typography color="text.secondary">
            {search
              ? "No projects found matching your search"
              : "No projects yet. Create a new project to get started."}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredProjects.map((p) => {
            const projectName = p.project_name || p.name;

            return (
              <Grid item xs={12} md={6} key={p.id}>
                <Card
                  onClick={() => handleCardClick(p)}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e8e8e8",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.04)",
                    p: 2,
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="start"
                  >
                    {/* Branding */}
                    <Box>
                      <Typography fontWeight={450}>
                        {projectName}
                      </Typography>
                      <Typography fontSize="13px" color="text.secondary">
                        {p.archetype
                          ? `Archetype: ${p.archetype}`
                          : "Custom Project"}
                      </Typography>
                    </Box>

                    {/* Status Chip and Delete Icon */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={p.workflow_status || "Yet-to-Start"}
                        color={getStatusColor(p.workflow_status)}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteClick(e, p)}
                        sx={{
                          color: "#d32f2f",
                          "&:hover": {
                            backgroundColor: "rgba(211, 47, 47, 0.08)",
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Optional Action Chip */}
                  {p.actionTag && (
                    <Chip
                      label={p.actionTag}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ mt: 1, fontWeight: 600 }}
                    />
                  )}

                  {/* CTA */}
                  <Button
                    variant="text"
                    sx={{
                      mt: 2,
                      fontWeight: 600,
                      color: "#0D66D0",
                      textTransform: "none",
                    }}
                    endIcon={<span style={{ fontWeight: 900 }}>&#8594;</span>}
                  >
                    View Governance Dashboard
                  </Button>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* NEW PROJECT DIALOG */}
      <Dialog
        open={showNewProjectDialog}
        onClose={() => !creating && setShowNewProjectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            fullWidth
            variant="outlined"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            disabled={creating}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !creating) {
                handleCreateProject();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowNewProjectDialog(false)}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            variant="contained"
            disabled={creating || !newProjectName.trim()}
          >
            {creating ? <CircularProgress size={24} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && handleDeleteCancel()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the project{" "}
            <strong>{projectToDelete?.project_name}</strong>? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectsPage;
